import React, { useState, useEffect } from 'react';
import { fixturesManager, oddsManager } from '../services/api/managers';
import { useNavigate } from 'react-router-dom';
import '../styles/index.css';
import 'react-calendar/dist/Calendar.css';

const leagueIds = [
  { id: 39, name: 'Premier League' },  
  { id: 2, name: 'Champions League' }, 
  { id: 78, name: 'Bundesliga' } 
];

const FixturesPage = () => {
  const [data, setData] = useState(null);
  const [allMatches, setAllMatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState('today');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(today.getDate() - 1);
        const tomorrow = new Date(today);
        tomorrow.setDate(today.getDate() + 1);

        const allMatches = [];

        for (const league of leagueIds) {
            const response = await fixturesManager.fetchFixturesByDateRange(league.id, yesterday, tomorrow, 2024);
            
            if (response && 
                response.data && 
                response.data.response && 
                response.data.response.length > 0) {
              const leagueFixtures = response.data.response;
              
              // Fetch odds for each fixture
              for (const fixture of leagueFixtures) {
                const oddsResponse = await oddsManager.fetchOddsByLeague(league.id, 2024, { fixture: fixture.fixture.id });
                fixture.odds = oddsResponse.data;
              }
              
              allMatches.push({
                league: league.name,
                fixtures: leagueFixtures
              });
            }
        }

        setAllMatches(allMatches);
        filterMatchesByDate(allMatches, selectedDate);
      } catch (err) {
        console.error('Error loading fixtures:', err);
        setError('Error loading data: ' + (err.message || 'Unknown error'));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const filterMatchesByDate = (matches, dateType) => {
    if (!matches || matches.length === 0) return;

    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);

    const filteredMatches = matches.map(league => {
      const dateToCheck = dateType === 'yesterday' ? yesterday : 
                        dateType === 'tomorrow' ? tomorrow : today;
      
      const filteredFixtures = league.fixtures.filter(fixture => {
        const fixtureDate = new Date(fixture.fixture.date);
        return fixtureDate.toDateString() === dateToCheck.toDateString();
      });

      return {
        league: league.league,
        fixtures: filteredFixtures
      };
    }).filter(league => league.fixtures.length > 0);

    setData({ response: filteredMatches });
  };

  const handleDateChange = (dateType) => {
    setSelectedDate(dateType);
    filterMatchesByDate(allMatches, dateType);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return 'Date not available'; // Placeholder for invalid dates
    }
    // Format date in English with explicit locale
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return date.toLocaleDateString('en-US', options);
  };

  const formatTime = (dateString) => {
    // Format time in English with explicit locale
    const options = { hour: '2-digit', minute: '2-digit', hour12: true };
    return new Date(dateString).toLocaleTimeString('en-US', options);
  };

  const handleMatchClick = (fixture, leagueName) => {
    // Navigate to match details page with fixture id and pass league name and time
    const formattedTime = formatTime(fixture.fixture.date);
    navigate(`/match/${fixture.fixture.id}`, {
      state: {
        league: leagueName,
        leagueId: fixture.league?.id,
        fixtureId: fixture.fixture.id,
        time: formattedTime,
        teams: fixture.teams,
        odds: {
          fixtureId: fixture.fixture.id
        }
      }
    });
  };

  const renderFixtures = () => {
    if (!data || !data.response || data.response.length === 0) {
      return (
        <div className="empty-data">
          <p>No matches available for the selected date</p>
        </div>
      );
    }
    
    return (
      <div className="fixtures-container">
        {data.response.map((leagueData, leagueIndex) => (
          <div key={`league-${leagueIndex}`} className="league-fixtures">
            <h3 className="league-name-header">{leagueData.league}</h3>
            <div className="fixtures-list">
              {leagueData.fixtures.map((fixture, fixtureIndex) => {
                if (!fixture || !fixture.fixture) return null;
                
                const formattedTime = formatTime(fixture.fixture.date);
                const formattedDate = formatDate(fixture.fixture.date);
                
                const league = fixture.league ? fixture.league : { name: 'Unknown League' };
                const round = fixture.league && fixture.league.round ? fixture.league.round : '';
                
                const venueName = fixture.fixture.venue && fixture.fixture.venue.name ? fixture.fixture.venue.name : 'Unknown Venue';
                const venueCity = fixture.fixture.venue && fixture.fixture.venue.city ? fixture.fixture.venue.city : '';
                
                const status = fixture.fixture.status?.short || 'NS';
                const statusText = 
                  status === 'FT' ? 'Finished' : 
                  status === 'NS' ? 'Not Started' : 
                  status === '1H' ? '1st Half' :
                  status === '2H' ? '2nd Half' :
                  status === 'HT' ? 'Half Time' :
                  status === 'ET' ? 'Extra Time' :
                  status === 'P' ? 'Penalties' :
                  status === 'SUSP' ? 'Suspended' :
                  status === 'INT' ? 'Interrupted' :
                  status === 'PST' ? 'Postponed' :
                  status === 'CANC' ? 'Cancelled' :
                  status === 'ABD' ? 'Abandoned' :
                  status === 'AWD' ? 'Technical Decision' :
                  status === 'WO' ? 'Walkover' :
                  status === 'LIVE' ? 'Live' :
                  'Unknown';
                
                return (
                  <div 
                    key={`fixture-${fixture.fixture.id || fixtureIndex}`} 
                    className="fixture-card-detailed"
                    onClick={() => handleMatchClick(fixture, league.name)}
                  >
                    <div className="fixture-header" style={{ display: 'flex', flexDirection: 'column' }}>
                      <div className="fixture-league">
                        {league.logo && (
                          <img 
                            src={league.logo} 
                            alt={league.name} 
                            className="league-logo-small" 
                            onError={(e) => {e.target.onerror = null; e.target.src = 'https://via.placeholder.com/25?text=L'}}
                          />
                        )}
                      </div>
                      <span className="league-name">{league.name}</span>
                      {round && <span className="fixture-round"> • {round}</span>}
                      <div className={`fixture-status status-${status.toLowerCase()}`}>
                        {status === 'NS' ? formattedTime : statusText}
                      </div>
                    </div>
                    
                    <div className="fixture-match">
                      <div className="team-block home-team-block">
                        <div className="team-logo">
                          <img 
                            src={fixture.teams?.home?.logo} 
                            alt={fixture.teams?.home?.name || 'Home Team'} 
                            className="team-logo-small"
                            onError={(e) => {e.target.onerror = null; e.target.src = 'https://via.placeholder.com/50?text=Team'}}
                          />
                        </div>
                        <div className="team-name" style={{
                          whiteSpace: 'normal', 
                          overflow: 'visible',
                          textOverflow: 'initial',
                          lineHeight: '1.2',
                          wordBreak: 'break-word',
                          height: 'auto',
                          minHeight: '40px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>{fixture.teams?.home?.name || 'Unknown team'}</div>
                      </div>
                      
                      <div className="match-score">
                        <span className="score-text">
                          {fixture.goals?.home !== null ? fixture.goals?.home : '-'} : {fixture.goals?.away !== null ? fixture.goals?.away : '-'}
                        </span>
                      </div>
                      
                      <div className="team-block away-team-block">
                        <div className="team-logo">
                          <img 
                            src={fixture.teams?.away?.logo} 
                            alt={fixture.teams?.away?.name || 'Away Team'} 
                            className="team-logo-small"
                            onError={(e) => {e.target.onerror = null; e.target.src = 'https://via.placeholder.com/50?text=Team'}}
                          />
                        </div>
                        <div className="team-name" style={{
                          whiteSpace: 'normal', 
                          overflow: 'visible',
                          textOverflow: 'initial',
                          lineHeight: '1.2',
                          wordBreak: 'break-word',
                          height: 'auto',
                          minHeight: '40px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>{fixture.teams?.away?.name || 'Unknown team'}</div>
                      </div>
                    </div>
                    
                    <div className="fixture-details">

                      <div className="fixture-time">
                        <i className="fa fa-clock-o" aria-hidden="true"></i> {formattedTime} on {formattedDate}
                      </div>                      
                      {fixture.fixture.venue && (
                        <div className="fixture-venue-detailed">
                          <i className="fa fa-map-marker" aria-hidden="true"></i>
                          <span>{venueName}{venueCity ? `, ${venueCity}` : ''}</span>
                        </div>
                      )}
                      
                      {fixture.fixture.referee && (
                        <div className="fixture-referee">
                          <i className="fa fa-user" aria-hidden="true"></i>
                          <span>Referee: {fixture.fixture.referee}</span>
                        </div>
                      )}
                    </div>
                    
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="data-page">        
      <div className="content-container">
        <div className="date-filters" style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          gap: '20px', 
          margin: '0 0 30px 0' 
        }}>
          <button 
            className={`date-filter-btn ${selectedDate === 'yesterday' ? 'active' : ''}`} 
            onClick={() => handleDateChange('yesterday')}
            style={{ 
              padding: '20px 20px', 
              borderRadius: '4px', 
              border: '1px solid #ddd',
              background: selectedDate === 'yesterday' ? '#3498db' : '#f5f5f5',
              color: selectedDate === 'yesterday' ? 'white' : '#333',
              cursor: 'pointer',
              fontWeight: 'bold'
            }}
          >
            Yesterday
          </button>
          <button 
            className={`date-filter-btn ${selectedDate === 'today' ? 'active' : ''}`} 
            onClick={() => handleDateChange('today')}
            style={{ 
              padding: '20px 20px', 
              borderRadius: '4px', 
              border: '1px solid #ddd',
              background: selectedDate === 'today' ? '#3498db' : '#f5f5f5',
              color: selectedDate === 'today' ? 'white' : '#333',
              cursor: 'pointer',
              fontWeight: 'bold'
            }}
          >
            Today
          </button>
          <button 
            className={`date-filter-btn ${selectedDate === 'tomorrow' ? 'active' : ''}`} 
            onClick={() => handleDateChange('tomorrow')}
            style={{ 
              padding: '20px 20px', 
              borderRadius: '4px', 
              border: '1px solid #ddd',
              background: selectedDate === 'tomorrow' ? '#3498db' : '#f5f5f5',
              color: selectedDate === 'tomorrow' ? 'white' : '#333',
              cursor: 'pointer',
              fontWeight: 'bold'
            }}
          >
            Tomorrow
          </button>
        </div>
  
        {loading ? (
          <div className="loading">Loading fixtures...</div>
        ) : error ? (
          <div className="error">{error}</div>
        ) : (
          <>
            {renderFixtures()}
          </>
        )}
      </div>
    </div>
  );
};

export default FixturesPage;